import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { PromptRegisterOptions } from '../models/prompt-register-options';
import { PromptRegisterListItem } from '../models/prompt-register-results.model';
import { PromptTypeOption } from '../models/prompt-type-option.interface';
import { PromptsFilterOption } from '../models/prompts-filter-option';
import * as promptRegisterActions from './prompt-register.actions';

export const promptRegisterFeatureKey = 'promptRegister';

export interface PromptRegisterState
   extends RegisterState<PromptRegisterListItem, PromptRegisterOptions, OrderingOption> {
   filters: PromptsFilterOption[];
   promptTypeOption: PromptTypeOption;
}

const adapter: RegisterAdapter<PromptRegisterListItem, PromptRegisterOptions, OrderingOption> = createRegisterAdapter<
   PromptRegisterListItem,
   PromptRegisterOptions,
   OrderingOption
>({
   selectId: (promptRegisterItem) => {
      return promptRegisterItem.promptId ?? 0;
   },
});

export const initialState: PromptRegisterState = {
   ...adapter.getInitialState(),
   ...adapter.getInitialState(),
   filters: [],
   promptTypeOption: { typeName: null },
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'name' }],
};

export const promptRegisterReducer = adapter.createReducer(
   promptRegisterActions.promptRegisterAdapterActions,
   initialState,
   on(
      promptRegisterActions.promptRegisterAdapterActions.pageEnter,
      promptRegisterActions.promptRegisterAdapterActions.loadPage,
      (state) => {
         return {
            ...state,
            callState: LoadingState.Loading,
         };
      }
   ),
   on(
      promptRegisterActions.promptRegisterAdapterActions.loadSuccess,
      promptRegisterActions.promptRegisterAdapterActions.loadPageSuccess,
      (state, action) => {
         return {
            ...state,
            callState: LoadingState.Loaded,
            endCursor: action.endCursor,
            prompts: action.items,
            hasNextPage: action.hasNextPage,
            totalCount: action.totalCount,
         };
      }
   ),
   on(promptRegisterActions.promptRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(promptRegisterActions.promptRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(promptRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: action.orderingOptions,
      };
   }),
   on(promptRegisterActions.promptRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: null,
      };
   })
);
export const promptRegisterSelector = createFeatureSelector<PromptRegisterState>(promptRegisterFeatureKey);
export const selectors = adapter.getSelectors(promptRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectQuickFilters,
   selectEndCursor,
   selectOrderingOptions,
} = adapter.getSelectors(promptRegisterSelector);
